import {
  ONBOARDING_STATE_KEY,
  createEmptyOnboardingState,
} from '@/config/onboardingWizard';
import { clearOnboardingCredentials } from '@/config/onboardingCredentials';

export function loadOnboardingState() {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveOnboardingState(state) {
  sessionStorage.setItem(ONBOARDING_STATE_KEY, JSON.stringify(state));
}

/**
 * Deep-merge known sections. Pass partial updates like { company: { city: 'London' } }.
 * @param {Record<string, unknown>} updates
 */
export function mergeOnboardingState(updates) {
  const current = loadOnboardingState() ?? createEmptyOnboardingState();
  const next = { ...current };

  if (updates.account != null) {
    next.account = { ...(current.account ?? {}), ...updates.account };
  }
  if (updates.company != null) {
    next.company = { ...(current.company ?? {}), ...updates.company };
  }
  if (updates.financial != null) {
    next.financial = { ...(current.financial ?? {}), ...updates.financial };
  }
  if (updates.locations != null) {
    next.locations = { ...(current.locations ?? {}), ...updates.locations };
    if (updates.locations.warehouses !== undefined) {
      next.locations.warehouses = updates.locations.warehouses;
    }
  }
  if (updates.team != null) {
    next.team = { ...(current.team ?? {}), ...updates.team };
    if (updates.team.invites !== undefined) {
      next.team.invites = updates.team.invites;
    }
    if (updates.team.role_templates !== undefined) {
      next.team.role_templates = updates.team.role_templates;
    }
  }

  saveOnboardingState(next);
  return next;
}

/**
 * Initialise wizard after registration step (account + passwords not stored here).
 * @param {{ company_name: string, first_name: string, last_name: string, email: string }} account
 */
export function initOnboardingFromRegistration(account) {
  const empty = createEmptyOnboardingState();
  const state = {
    ...empty,
    account: { ...account },
    company: {
      ...empty.company,
      registered_name: account.company_name ?? '',
      billing_email: account.email ?? '',
    },
  };
  saveOnboardingState(state);
  return state;
}

export function clearOnboardingState() {
  sessionStorage.removeItem(ONBOARDING_STATE_KEY);
  clearOnboardingCredentials();
}
